import React from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import OwnerClientPlatformDetail from "./clients/OwnerClientPlatformDetail";
import OwnerClientLinkDialog from "./clients/OwnerClientLinkDialog";
import { surfaceSx } from "./center/ownerCenterUi";

function idOf(value) {
  return String(value?.id || value?._id || "");
}

const CRM_STAGE_LABELS = { lead: "Lead", contacted: "Contactado", qualified: "Calificado", proposal: "Propuesta", negotiation: "Negociación", won: "Ganado", onboarding: "Onboarding", customer: "Cliente", lost: "Perdido" };
const CRM_STAGE_ORDER = { lead: 0, contacted: 1, qualified: 2, proposal: 3, negotiation: 4, won: 5, onboarding: 6, customer: 7, lost: 8 };

export default function OwnerClientsPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedClientId = String(searchParams.get("cliente") || "");
  const [clients, setClients] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [crmStageFilter, setCrmStageFilter] = React.useState("");
  const [selectedId, setSelectedId] = React.useState("");
  const [platformContext, setPlatformContext] = React.useState(null);
  const [crmContext, setCrmContext] = React.useState(null);
  const [loadingList, setLoadingList] = React.useState(true);
  const [loadingPlatform, setLoadingPlatform] = React.useState(false);
  const [loadingCrm, setLoadingCrm] = React.useState(false);
  const [error, setError] = React.useState("");
  const [platformError, setPlatformError] = React.useState("");
  const [crmError, setCrmError] = React.useState("");
  const [linkOpen, setLinkOpen] = React.useState(false);
  const platformRequestRef = React.useRef(0);
  const crmRequestRef = React.useRef(0);

  const loadClients = React.useCallback(async () => {
    setLoadingList(true);
    setError("");
    try {
      const response = await ownerApi.listClients({ limit: 250 });
      const rows = response?.clients || [];
      setClients(rows);
      setSelectedId((current) => {
        if (requestedClientId && rows.some((row) => idOf(row) === requestedClientId)) return requestedClientId;
        if (current && rows.some((row) => idOf(row) === current)) return current;
        return idOf(rows[0]);
      });
    } catch (err) {
      setError(formatApiError(err, "No pude cargar tus clientes."));
    } finally {
      setLoadingList(false);
    }
  }, [requestedClientId]);

  const loadPlatform = React.useCallback(async (clientId, { clear = false } = {}) => {
    const requestId = ++platformRequestRef.current;
    if (!clientId) {
      setPlatformContext(null);
      setPlatformError("");
      setLoadingPlatform(false);
      return;
    }
    if (clear) setPlatformContext(null);
    setLoadingPlatform(true);
    setPlatformError("");
    try {
      const response = await ownerApi.clientWebProfitContext(clientId);
      if (requestId === platformRequestRef.current) setPlatformContext(response?.context || null);
    } catch (err) {
      if (requestId === platformRequestRef.current) setPlatformError(formatApiError(err, "No pude cargar la relación con WebProfit."));
    } finally {
      if (requestId === platformRequestRef.current) setLoadingPlatform(false);
    }
  }, []);

  const loadCrm = React.useCallback(async (clientId, { clear = false } = {}) => {
    const requestId = ++crmRequestRef.current;
    if (!clientId) {
      setCrmContext(null);
      setCrmError("");
      setLoadingCrm(false);
      return;
    }
    if (clear) setCrmContext(null);
    setLoadingCrm(true);
    setCrmError("");
    try {
      const response = await ownerApi.personContext(clientId);
      if (requestId === crmRequestRef.current) setCrmContext(response?.context || null);
    } catch (err) {
      if (requestId === crmRequestRef.current) setCrmError(formatApiError(err, "No pude cargar actividad, dinero y horas del cliente."));
    } finally {
      if (requestId === crmRequestRef.current) setLoadingCrm(false);
    }
  }, []);

  React.useEffect(() => {
    loadClients();
  }, [loadClients]);

  React.useEffect(() => {
    if (requestedClientId && clients.some((row) => idOf(row) === requestedClientId)) setSelectedId(requestedClientId);
  }, [clients, requestedClientId]);

  React.useEffect(() => {
    if (!selectedId) {
      setCrmContext(null);
      setPlatformContext(null);
      return;
    }
    loadCrm(selectedId, { clear: true });
    loadPlatform(selectedId, { clear: true });
  }, [selectedId, loadCrm, loadPlatform]);

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = clients
    .filter((client) => !normalizedQuery || [client.name, client.company, client.email, client.phone].some((value) => String(value || "").toLowerCase().includes(normalizedQuery)))
    .filter((client) => !crmStageFilter || String(client?.crm?.stage || (client.relationshipType === "prospect" ? "lead" : "customer")) === crmStageFilter)
    .sort((a, b) => {
      const stageA = String(a?.crm?.stage || (a.relationshipType === "prospect" ? "lead" : "customer"));
      const stageB = String(b?.crm?.stage || (b.relationshipType === "prospect" ? "lead" : "customer"));
      const stageDiff = Number(CRM_STAGE_ORDER[stageA] ?? 99) - Number(CRM_STAGE_ORDER[stageB] ?? 99);
      if (stageDiff) return stageDiff;
      const actionA = a?.crm?.nextActionAt ? new Date(a.crm.nextActionAt).getTime() : Number.MAX_SAFE_INTEGER;
      const actionB = b?.crm?.nextActionAt ? new Date(b.crm.nextActionAt).getTime() : Number.MAX_SAFE_INTEGER;
      if (actionA !== actionB) return actionA - actionB;
      return String(a.name || "").localeCompare(String(b.name || ""), "es");
    });
  const selected = clients.find((row) => idOf(row) === selectedId) || null;
  const jarvisUi = location.state?.ownerJarvisUi || null;
  const identityEditToken = jarvisUi?.surface === "client"
    && String(jarvisUi?.clientId || "") === String(selectedId || "")
    && jarvisUi?.form === "client_edit"
    ? String(jarvisUi?.token || `${selectedId}-edit`)
    : "";

  const handleClientUpdated = (nextClient) => {
    const nextId = idOf(nextClient) || selectedId;
    setClients((current) => current.map((row) => idOf(row) === nextId ? { ...row, ...nextClient } : row));
    if (nextId === selectedId) loadPlatform(selectedId);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1600, mx: "auto", pb: 1 }}>
      <Box sx={{ ...surfaceSx, px: 1.25, py: .85, mb: 1 }}>
        <Typography sx={{ fontWeight: 1000, fontSize: ".9rem" }}>Personas y cuentas</Typography>
        <Typography sx={{ mt: .15, color: "text.secondary", fontWeight: 720, fontSize: ".7rem" }}>Acá vive la identidad y la relación. El trabajo, contratos, billing, cobros, bloqueos y agenda viven en cada Proyecto.</Typography>
      </Box>
      {error ? <Alert severity="error" sx={{ mb: 1.2 }}>{error}</Alert> : null}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(300px, 380px) minmax(0, 1fr)" }, gap: 1.4, alignItems: "start" }}>
        <Box sx={{ ...surfaceSx, p: 1.2, position: { lg: "sticky" }, top: { lg: 0 }, maxHeight: { lg: "calc(100dvh - 124px)" }, overflow: "hidden" }}>
          <Stack direction={{ xs: "column", sm: "row", lg: "column" }} spacing={.75}>
            <TextField fullWidth size="small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar persona, empresa, email…" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }} />
            <FormControl fullWidth size="small">
              <InputLabel>Pipeline</InputLabel>
              <Select label="Pipeline" value={crmStageFilter} onChange={(event) => setCrmStageFilter(event.target.value)}>
                <MenuItem value="">Todas las etapas</MenuItem>
                {Object.entries(CRM_STAGE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Typography sx={{ px: .6, pt: 1.1, pb: .65, color: "text.secondary", fontSize: ".69rem", fontWeight: 900 }}>{filtered.length} PERSONAS · ORDENADAS POR PIPELINE</Typography>
          <Stack spacing={.45} sx={{ maxHeight: { lg: "calc(100dvh - 212px)" }, overflowY: "auto", pr: .2 }}>
            {loadingList ? <Box sx={{ py: 7, textAlign: "center" }}><CircularProgress size={28} /></Box> : filtered.map((client) => {
              const id = idOf(client);
              const active = id === selectedId;
              return (
                <Box
                  key={id}
                  role="button"
                  tabIndex={0}
                  aria-current={active ? "true" : undefined}
                  onClick={() => { setSelectedId(id); setSearchParams({ cliente: id }, { replace: true }); }}
                  onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedId(id); setSearchParams({ cliente: id }, { replace: true }); } }}
                  sx={{ px: 1.15, py: .95, borderRadius: 2.2, cursor: "pointer", bgcolor: active ? "action.selected" : "transparent", border: "1px solid", borderColor: active ? "primary.main" : "transparent", transition: "120ms ease", "&:hover": { bgcolor: active ? "action.selected" : "action.hover" } }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "action.hover", flex: "0 0 auto" }}><PersonRoundedIcon sx={{ fontSize: 18 }} /></Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontSize: ".84rem", fontWeight: 950 }}>{client.name}</Typography>
                      <Typography noWrap sx={{ color: "text.secondary", fontSize: ".7rem" }}>{client.company || client.email || client.relationshipType || "Persona"} · {CRM_STAGE_LABELS[client?.crm?.stage] || (client.relationshipType === "prospect" ? "Lead" : "Cliente")}</Typography>
                    </Box>
                    {client.webProfitUserId ? <CloudDoneRoundedIcon color="success" sx={{ fontSize: 17 }} aria-label="Cuenta WebProfit vinculada" /> : null}
                  </Stack>
                </Box>
              );
            })}
            {!loadingList && !filtered.length ? <Typography sx={{ py: 5, textAlign: "center", color: "text.secondary", fontSize: ".8rem" }}>No encontré personas.</Typography> : null}
          </Stack>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          {selected ? (
            <OwnerClientPlatformDetail
              client={selected}
              platformContext={platformContext}
              crmContext={crmContext}
              loadingCrm={loadingCrm}
              loadingPlatform={loadingPlatform}
              crmError={crmError}
              platformError={platformError}
              onLinkUser={() => setLinkOpen(true)}
              onRefreshCrm={() => loadCrm(selectedId)}
              onRefreshPlatform={() => loadPlatform(selectedId)}
              onClientUpdated={handleClientUpdated}
              onPlatformContextUpdated={(nextContext) => setPlatformContext(nextContext)}
              identityEditToken={identityEditToken}
            />
          ) : <Box sx={{ ...surfaceSx, py: 12, textAlign: "center" }}><Typography sx={{ color: "text.secondary" }}>Elegí una persona para ver su ficha 360.</Typography></Box>}
        </Box>
      </Box>

      <OwnerClientLinkDialog
        open={linkOpen}
        client={selected}
        context={platformContext}
        onClose={() => setLinkOpen(false)}
        onLinked={(next, linkedClient) => {
          setPlatformContext(next);
          if (linkedClient) {
            const linkedId = idOf(linkedClient);
            setClients((current) => current.map((row) => idOf(row) === linkedId ? { ...row, ...linkedClient } : row));
          }
        }}
      />
    </Box>
  );
}
